import type { Kennzahl, DashboardValue } from './types'

/**
 * Aggregiert Semesterwerte einer Kennzahl auf Studienjahres-Ebene.
 *
 * Studienjahr YYYY = SoSe YYYY (year-Code YYYY1) + WiSe YYYY/YY+1 (year-Code YYYY2).
 * Beide Semester haben denselben YYYY-Anteil: `Math.floor(yearCode / 10)`.
 *
 * Im Gegensatz zu semesterbasierten Strategien arbeitet diese Funktion direkt auf
 * den ungefilterten Rohdaten (`dashboardData`), damit auch Jahre ohne Datenpunkte
 * auf der Achse erscheinen (Wert 0).
 *
 * @param kennzahl      - Kennzahl-Konfiguration (sourceKennzahlId zeigt auf Semesterdaten)
 * @param dashboardData - ungefilterte Rohdaten aus dem Store
 * @param selectedType  - aktuell ausgewählter Diagrammtyp (bestimmt die Farbe)
 * @param yearsCount    - Anzahl der darzustellenden Jahre (abgeleitet aus yearsToDisplay / 2)
 */
export function buildYearlyAggregateChartData(
    kennzahl: Kennzahl,
    dashboardData: DashboardValue[],
    selectedType: string,
    yearsCount: number
): any {
    const sourceId = kennzahl.sourceKennzahlId ?? kennzahl.kennzahlId
    const allValues = dashboardData.filter((d) => d.kennzahlId === sourceId)

    // Aktuellstes Jahr aus den Daten ableiten; Fallback auf das aktuelle Kalenderjahr
    const currentYear =
        allValues.length > 0
            ? Math.max(...allValues.map((v) => Math.floor(v.year / 10)))
            : new Date().getFullYear()

    const startYear = currentYear - yearsCount + 1
    const yearRange = Array.from({ length: yearsCount }, (_, i) => startYear + i)

    // Semesterwerte nach Studienjahr (YYYY) gruppieren und summieren
    const yearMap = new Map<number, number>()
    allValues.forEach((v) => {
        const yyyy = Math.floor(v.year / 10)
        if (yyyy >= startYear && yyyy <= currentYear) {
            yearMap.set(yyyy, (yearMap.get(yyyy) ?? 0) + Number(v.wert))
        }
    })

    const data: { x: string; y: number }[] = yearRange.map((yyyy) => ({
        x: String(yyyy),
        y: yearMap.get(yyyy) ?? 0
    }))

    const maxRaw = Math.max(...data.map((d) => d.y))
    const maxValue = maxRaw ? maxRaw * 1.15 : 10

    return {
        labels: yearRange.map(String),
        datasets: [
            {
                label: 'Einschreibungen',
                data,
                backgroundColor: selectedType === 'LineChart' ? '#E53009' : '#009EE3',
                borderColor: selectedType === 'LineChart' ? '#E53009' : '#009EE3',
                fill: false,
                maxBarThickness: 41,
                minBarLength: 7,
                isPercentValue: false,
                maxValue
            }
        ]
    }
}
